import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, ArrowUp, ArrowDown, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useSite } from '@/context/SiteContext';
import { errorMessage } from '@/lib/errorMessage';

export default function SiteMenuPanel() {
    const { menu, reload } = useSite();
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setItems(menu || []); }, [menu]);

    const update = (i, patch) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
    const move = (i, dir) => setItems((arr) => {
        const j = i + dir;
        if (j < 0 || j >= arr.length) return arr;
        const next = [...arr];
        [next[i], next[j]] = [next[j], next[i]];
        return next.map((it, idx) => ({ ...it, order: idx }));
    });
    const addItem = () => setItems((arr) => [...arr, { key: `new-${Date.now()}`, label: 'Nuova voce', to: '/', hidden: false, order: arr.length }]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = items.map((it, idx) => ({ ...it, order: idx }));
            await api.put('/admin/site/menu', { items: payload });
            toast.success('Menu salvato');
            await reload();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore salvataggio'));
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-menu-panel">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-serif text-xl">Menu di navigazione</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Ordina, rinomina, nascondi o rimuovi le voci del menu di header e footer.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={addItem} className="h-9"><Plus className="h-3.5 w-3.5 mr-1.5" />Aggiungi</Button>
                    <Button onClick={save} disabled={saving} data-testid="admin-menu-save" className="h-9 px-4">
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Salva
                    </Button>
                </div>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                {items.length === 0 ? (
                    <p className="p-6 text-center text-sm text-muted-foreground">Menu vuoto — aggiungi una voce.</p>
                ) : items.map((it, i) => (
                    <div key={it.key + i} className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 items-center px-4 py-3 border-b border-border last:border-0">
                        <div className="flex flex-col gap-0.5">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => move(i, -1)}><ArrowUp className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => move(i, 1)}><ArrowDown className="h-3 w-3" /></Button>
                        </div>
                        <div>
                            <Label className="eyebrow text-[10px]">Etichetta</Label>
                            <Input value={it.label} onChange={(e) => update(i, { label: e.target.value })} className="mt-0.5 h-9" />
                        </div>
                        <div>
                            <Label className="eyebrow text-[10px]">URL (es. /atelier)</Label>
                            <Input value={it.to} onChange={(e) => update(i, { to: e.target.value })} className="mt-0.5 h-9 font-mono text-xs" />
                        </div>
                        <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => update(i, { hidden: !it.hidden })} title={it.hidden ? 'Mostra' : 'Nascondi'}>
                                {it.hidden ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-end pt-2">
                <Button onClick={save} disabled={saving} className="h-11 px-5">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Salva menu
                </Button>
            </div>
        </div>
    );
}
